"use client"

import * as React from "react"
import Link from "next/link"
import { ArrowLeftIcon, SendHorizontalIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import type { Client, Message } from "@/lib/data/types"

/**
 * Trois amorces pour enlever le premier mot — comme côté coach.
 *
 * À 21 h, un champ vide fait remettre à demain le message qu'on voulait
 * envoyer. Elles n'écrivent pas à la place de la cliente, elles ouvrent la
 * porte : une question, un ressenti, une gêne à signaler.
 */
const AMORCES = [
  "J'ai une question…",
  "Cette semaine, je me sens…",
  "Un petit souci :",
]

/**
 * Le fil — la première fois que la cliente a une *voix* dans Cairn.
 *
 * Tout le reste de l'app va dans un sens : le coach dépose, la cliente coche.
 * Ici c'est un aller-retour. C'est le canal « entre les deux » qui fait qu'on
 * ne suit pas un programme seul·e mais *avec quelqu'un* — on pose une question,
 * on signale une gêne, on partage une fierté, et le coach est là.
 *
 * Honnête comme une maquette doit l'être : l'historique est de la démo
 * explicite ; un message envoyé s'affiche vraiment, mais l'app ne fabrique
 * jamais une réponse du coach à sa place. Elle dit franchement qu'il répondra.
 */
export function Fil({ client }: { client: Client }) {
  const [messages, setMessages] = React.useState<Message[]>(client.fil)
  const [texte, setTexte] = React.useState("")
  const champRef = React.useRef<HTMLTextAreaElement>(null)
  const finRef = React.useRef<HTMLDivElement>(null)

  /* Après un envoi, on descend jusqu'au dernier message : le fil se lit du bas,
     comme une conversation. On ne remue pas la page au premier rendu. */
  const monte = React.useRef(false)
  React.useEffect(() => {
    if (monte.current) finRef.current?.scrollIntoView({ behavior: "smooth" })
    monte.current = true
  }, [messages.length])

  const dernier = messages[messages.length - 1]
  const attenteCoach = dernier?.auteur === "client"

  function ajouterAmorce(amorce: string) {
    setTexte((actuel) => (actuel ? `${actuel.trimEnd()} ${amorce}` : amorce))
    champRef.current?.focus()
  }

  function envoyer(event: React.FormEvent) {
    event.preventDefault()
    const propre = texte.trim()
    if (!propre) return

    /* On l'affiche pour de vrai, tout de suite. Local à la session : une
       maquette n'a pas de back-end, et l'historique semé porte déjà le récit.
       Aucune réponse du coach n'est inventée — ce serait faire passer du faux
       pour du vrai. On dit juste, sobrement, qu'il répondra. */
    setMessages((actuels) => [
      ...actuels,
      {
        id: `local-${actuels.length}`,
        auteur: "client",
        texte: propre,
        quand: "À l'instant",
      },
    ])
    setTexte("")
  }

  return (
    <div className="flex min-h-[100dvh] flex-col pb-48">
      <div className="mx-auto flex w-full max-w-[560px] flex-1 flex-col px-gutter">
        {/* -------------------------------------------------------- en-tête */}
        <header className="flex items-center justify-between pt-8">
          <Link
            href="/mon-espace"
            className="flex items-center gap-2 rounded-sm text-faint outline-none transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <ArrowLeftIcon aria-hidden className="size-4" />
            <span className="font-heading text-xs tracking-brand uppercase">
              Aujourd&apos;hui
            </span>
          </Link>
          <span className="font-heading text-xs tracking-brand text-faint uppercase">
            Le fil
          </span>
        </header>

        <div className="pt-10">
          <p className="font-heading text-xs tracking-brand text-faint uppercase">
            Votre échange
          </p>
          <h1 className="mt-3 flex items-center gap-3 font-heading text-[clamp(1.75rem,6vw,2.5rem)] leading-none font-medium uppercase">
            <span
              aria-hidden
              className="grid size-9 shrink-0 place-items-center rounded-full border border-border-strong bg-foreground/10 text-[0.9375rem] text-foreground"
            >
              {client.coach.charAt(0)}
            </span>
            {client.coach}
          </h1>
          <p className="mt-3 text-[0.9375rem] text-muted-foreground text-pretty">
            Entre deux séances, {client.coach} reste joignable ici — une
            question, une gêne, un doute. Rien n&apos;est perdu.
          </p>
        </div>

        {/* -------------------------------------------------------- le fil */}
        <ol className="mt-10 flex flex-1 flex-col gap-4">
          {messages.map((message) => (
            <Bulle key={message.id} message={message} coach={client.coach} />
          ))}

          {/* Le dernier mot est à la cliente : on rassure sans rien inventer. */}
          {attenteCoach ? (
            <li
              role="status"
              className="mt-1 text-center font-heading text-xs tracking-brand text-faint uppercase"
            >
              {client.coach} vous répondra bientôt
            </li>
          ) : null}

          <div ref={finRef} />
        </ol>
      </div>

      {/* ------------------------------------------------------- l'écriture */}
      {/* Fixée en bas, au-dessus de la navigation : le geste d'écrire est
          toujours à portée de pouce, sans remonter le fil. */}
      <form
        onSubmit={envoyer}
        className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)_+_4rem)] z-30 border-t border-border bg-background/95 backdrop-blur-sm"
      >
        <div className="mx-auto w-full max-w-[560px] px-gutter py-3">
          <div className="mb-2 flex flex-wrap gap-2">
            {AMORCES.map((amorce) => (
              <button
                key={amorce}
                type="button"
                onClick={() => ajouterAmorce(amorce)}
                className="rounded-sm border border-border px-3 py-1.5 text-[0.8125rem] text-muted-foreground outline-none transition-colors hover:border-border-strong hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                {amorce}
              </button>
            ))}
          </div>

          <div className="flex items-end gap-2">
            <textarea
              ref={champRef}
              rows={1}
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              onKeyDown={(e) => {
                // Entrée envoie ; Maj+Entrée passe à la ligne. Le geste rapide
                // d'une conversation, pas d'un formulaire.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  envoyer(e)
                }
              }}
              placeholder={`Écrire à ${client.coach}…`}
              aria-label={`Écrire à ${client.coach}`}
              className="max-h-32 min-h-11 flex-1 resize-none rounded-lg border border-border-strong bg-card px-4 py-2.5 text-[0.9375rem] leading-relaxed outline-none transition-colors placeholder:text-faint focus-visible:ring-3 focus-visible:ring-ring/50"
            />
            <button
              type="submit"
              disabled={!texte.trim()}
              aria-label="Envoyer"
              className="grid size-11 shrink-0 place-items-center rounded-lg border border-foreground bg-foreground text-background outline-none transition-colors hover:bg-foreground/90 focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-40"
            >
              <SendHorizontalIcon aria-hidden className="size-4" />
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

/* ------------------------------------------------------------- morceaux */

/**
 * Une bulle du fil. Le coach à gauche avec son initiale ; la cliente à droite,
 * son message posé en pleine couleur — c'est sa voix, elle se lit d'un coup.
 */
function Bulle({ message, coach }: { message: Message; coach: string }) {
  const duCoach = message.auteur === "coach"

  return (
    <li
      className={cn(
        "flex flex-col gap-1",
        duCoach ? "items-start" : "items-end"
      )}
    >
      <div
        className={cn(
          "flex max-w-[85%] gap-3",
          duCoach ? "flex-row" : "flex-row-reverse"
        )}
      >
        {duCoach ? (
          <span
            aria-hidden
            className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full border border-border-strong bg-foreground/10 font-heading text-xs text-foreground"
          >
            {coach.charAt(0)}
          </span>
        ) : null}
        <p
          className={cn(
            "rounded-lg px-4 py-3 text-[1rem] leading-relaxed text-pretty",
            duCoach
              ? "border border-border bg-card"
              : "bg-foreground text-background"
          )}
        >
          <span className="sr-only">
            {duCoach ? `${coach} : ` : "Vous : "}
          </span>
          {message.texte}
        </p>
      </div>
      <span
        className={cn(
          "font-heading text-[0.6875rem] tracking-brand text-faint uppercase",
          duCoach ? "pl-10" : "pr-1"
        )}
      >
        {message.quand}
      </span>
    </li>
  )
}
